"use client";

import Link from "next/link";
import React from "react";
import Image from "next/image";
import ImageWrapper from "@/component/ui/ImageWrapper";

import type {
	SearchDropdownData,
	SearchIcons,
	SponsoredItem,
	SimpleLinkItem,
	TrendingItem,
} from "@/app/utilities/types/searchDropdown";

/* =========================================
   PROPS
========================================= */
type SearchDropdownRevampProps = {
	query?: string;
	isMobile?: boolean;
	isTyping?: boolean;
	data: SearchDropdownData;
	onOptionClick?: () => void;
};

export type SearchSuggestionItem = {
	title?: string;
	url?: string;
	page_type?: string;
	brand?: string;
	model?: string;
	city?: string;
};

export type SearchLocation = {
	name: string;
	slug: string;
};

type Step2SectionKey = keyof SearchDropdownData["step2Data"];

type Step2SectionConfig = {
	key: Step2SectionKey;
	title?: string;
	items: SimpleLinkItem[];
};

const STEP2_SECTION_ORDER: Step2SectionKey[] = [
	"models",
	"utility",
	"listings",
	"comparisons",
	"editorial",
];

const SECTION_TITLE_BY_KEY: Partial<Record<Step2SectionKey, string>> = {
	models: "Models",
	utility: "Utility & Pricing",
	listings: "Listings",
	comparisons: "Comparisons",
	editorial: "Editorial",
};

const normalizeSectionKey = (section: string): Step2SectionKey | null => {
	if (section === "compare") return "comparisons";
	if (section === "content") return "editorial";
	if (STEP2_SECTION_ORDER.includes(section as Step2SectionKey)) {
		return section as Step2SectionKey;
	}
	return null;
};

const getOrderedStep2Sections = (
	step2Data: SearchDropdownData["step2Data"],
	sectionRanking: SearchDropdownData["sectionRanking"]
): Step2SectionConfig[] => {
	const rankedSections: Step2SectionKey[] = [];

	[...sectionRanking]
		.sort((left, right) => left.rank - right.rank)
		.forEach((entry) => {
			const key = normalizeSectionKey(entry.section);
			if (key && !rankedSections.includes(key)) {
				rankedSections.push(key);
			}
		});

	const fallbackSections = STEP2_SECTION_ORDER.filter(
		(key) => !rankedSections.includes(key)
	);

	return [...rankedSections, ...fallbackSections]
		.map((key) => ({
			key,
			title: SECTION_TITLE_BY_KEY[key],
			items: step2Data[key],
		}))
		.filter((section) => section.items.length > 0);
};

const getSectionRowProps = (
	sectionKey: Step2SectionKey,
	icons: SearchIcons
) => {
	switch (sectionKey) {
		case "models":
			return { leftIcon: icons.car, boxed: true, showArrow: true };
		case "utility":
			return { leftIcon: icons.price };
		case "listings":
			return { leftIcon: icons.price };
		case "comparisons":
			return { leftIcon: icons.compare };
		case "editorial":
			return { leftIcon: icons.web };
	}
};

const renderStep2Section = (
	section: Step2SectionConfig,
	query: string,
	icons: SearchIcons,
	isMobile: boolean,
	addTopMargin: boolean
) => {
	const RowComponent = isMobile ? MobileRow : Row;
	const rowProps = getSectionRowProps(section.key, icons);

	return (
		<div
			key={section.key}
			className={addTopMargin ? "mt-4" : undefined}
		>
			{section.title && <SectionTitle>{section.title}</SectionTitle>}
			{section.items.map((item, index) => (
				<RowComponent
					key={section.key === "comparisons" || section.key === "editorial" ? `${item.link}-${index}` : item.label}
					item={item}
					query={query}
					icons={icons}
					{...rowProps}
				/>
			))}
		</div>
	);
};

const buildPriceUtilityItem = (
	models: SearchSuggestionItem[],
	utilityItems: SearchSuggestionItem[],
	location: SearchLocation | null,
	priceIcon: string
): SimpleLinkItem | null => {
	if (!location) {
		return null;
	}

	const existingPriceItem = utilityItems.find((item) => item.page_type === "price");
	if (existingPriceItem?.title && existingPriceItem.url) {
		return {
			label: existingPriceItem.title,
			link: existingPriceItem.url,
			icon: priceIcon,
		};
	}

	const modelMatch = models.find(
		(item) => item.page_type === "model_pdp" && item.brand && item.model && item.url
	);

	if (!modelMatch?.brand || !modelMatch.model || !modelMatch.url) {
		return null;
	}

	return {
		label: `${modelMatch.brand} ${modelMatch.model} Price in ${location.name}`,
		link: `${modelMatch.url.replace(/\/$/, "")}/price-in-${location.slug}`,
		icon: priceIcon,
	};
};

export const buildUtilityPricingItems = (
	models: SearchSuggestionItem[],
	utilityItems: SearchSuggestionItem[],
	location: SearchLocation | null,
	icons: Pick<SearchIcons, "price" | "emi">
): SimpleLinkItem[] => {
	const mappedUtilityItems: SimpleLinkItem[] = utilityItems.map((item) => {
		let icon = icons.price;
		if (item.page_type === "emi") icon = icons.emi;
		return {
			label: item.title || "",
			link: item.url || "/",
			icon,
		};
	});

	if (utilityItems.length === 0) {
		return mappedUtilityItems;
	}

	const priceUtilityItem = buildPriceUtilityItem(
		models,
		utilityItems,
		location,
		icons.price
	);

	if (!priceUtilityItem) {
		return mappedUtilityItems;
	}

	return [
		// place priceUtilityItem on bottom of the list, but ensure it is unique (no duplicates)
		// ...mappedUtilityItems.filter((item) => item.link !== priceUtilityItem.link),
		// priceUtilityItem,
		...mappedUtilityItems,
	];
};

/* =========================================
   CDN HELPER
========================================= */
const CDN = process.env.NEXT_PUBLIC_CDN_STATIC_URL;

const iconSrc = (filename: string) =>
	`${CDN}search-icons/${filename}`;

/* =========================================
   MATCH HELPERS
========================================= */
const matchesQuery = (text: string, query: string): boolean =>
	text.toLowerCase().includes(query.toLowerCase().trim());

const hasStep2Match = (
	query: string,
	step2Data: SearchDropdownData["step2Data"]
): boolean => {
	const q = query.trim();
	if (!q) return false;
	return [
		...step2Data.models,
		...step2Data.utility,
		...step2Data.listings,
		...step2Data.comparisons,
		...step2Data.editorial,
	].some((item) => matchesQuery(item.label, q));
};

const hasStep2Content = (
	step2Data: SearchDropdownData["step2Data"]
): boolean =>
	[
		step2Data.models,
		step2Data.utility,
		step2Data.listings,
		step2Data.comparisons,
		step2Data.editorial,
	].some((items) => items.length > 0);

/* =========================================
   SHARED UI ATOMS
========================================= */
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
	<h3 className="text-xs font-bold text-[#737685] tracking-widest uppercase mb-2">
		{children}
	</h3>
);

const Arrow = ({ icons }: { icons: SearchIcons }) => (
	<Image
		src={iconSrc(icons.arrow)}
		alt="trending-icon"
		width={20}
		height={12}
		className="flex-shrink-0 mt-1"
	/>
);

const RightArrow = ({ icons }: { icons: SearchIcons }) => (
	<Image
		src={iconSrc(icons.rightarrow)}
		alt="arrow"
		width={12}
		height={12}
		className="w-3 h-3 flex-shrink-0 ml-2"
	/>
);

/* =========================================
   HIGHLIGHT HELPER
========================================= */
const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
	if (!query.trim()) {
		return (
			<span className="font-roboto font-medium text-[16px] leading-[24px] text-[#141B2C] whitespace-nowrap">
				{text}
			</span>
		);
	}

	const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const parts = text.split(new RegExp(`(${safeQuery})`, "gi"));

	return (
		<span className="whitespace-nowrap font-roboto font-[500] md:font-medium text-[13px] md:text-[14px] leading-[24px] text-[#141B2C]">
			{parts.map((part, i) =>
				part.toLowerCase() === query.toLowerCase() ? (
					<span key={i} className="text-[#008C8B] font-bold">
						{part}
					</span>
				) : (
					part
				)
			)}
		</span>
	);
};

/* =========================================
   SHARED — LEFT ICON BOX
========================================= */
const IconBox = ({
	item,
	leftIcon,
	boxed,
}: {
	item: SimpleLinkItem;
	leftIcon: string;
	boxed: boolean;
}) => (
	<div
		className={[
			"flex items-center justify-center flex-shrink-0 overflow-hidden",
			boxed ? "w-14 h-11 bg-gray-800 rounded-xl" : "w-4 h-4",
		].join(" ")}
	>
		{boxed && item.img ? (
			<ImageWrapper
				src={item.img}
				alt={item.label}
				width={56}
				height={44}
				className="w-full h-full object-fill rounded-xl"
			/>
		) : (
			<Image src={iconSrc(leftIcon)} alt="left-icon" width={16} height={16} className="w-4 h-4" />
		)}
	</div>
);

/* =========================================
   DESKTOP — NO RESULT BANNER
========================================= */
const NoResultBanner = ({
	query,
	icons,
}: {
	query: string;
	icons: SearchIcons;
}) => (
	<div className="flex items-center gap-3 bg-[#000000AB] text-white px-4 py-3 rounded-t-[12px]">
		<span className="flex-shrink-0 flex items-center justify-center">
			<Image src={iconSrc(icons.exclamatory)} alt="!" width={16} height={16} />
		</span>
		<p className="font-roboto font-[500] text-[12px] leading-[16px] tracking-[0.3px]">
			We couldn&apos;t find anything matching &quot;
			<span className="font-[700]">{query}</span>&quot; Try a different Keyword.
		</p>
	</div>
);

/* =========================================
   DESKTOP — SPONSORED CARD
========================================= */
const SponsoredCard = ({ item }: { item: SponsoredItem }) => (
	<div className="flex items-center gap-3 py-3 mb-2">
		<Link href={item.redirect_url || "/"}>
			<ImageWrapper
				src={item.img}
				alt={item.title}
				width={80}
				height={80}
				className="w-[80px] h-[80px] object-fill rounded-lg flex-shrink-0"
			/>
		</Link>
		<div>
			<p className="font-roboto font-semibold text-[12px] leading-[16px] uppercase text-[#008C8B] mb-1">
				{item.label}
			</p>
			<Link
				href={item.redirect_url || "/"}
				className="font-roboto font-medium text-[16px] leading-[24px] text-[#141B2C]"
			>
				{item.title}
			</Link>
			{item.subtitle && (
				<p className="font-roboto font-normal text-[12px] leading-[16px] text-[#525F73]">
					{item.subtitle}
				</p>
			)}
		</div>
	</div>
);

/* =========================================
   DESKTOP — TRENDING ROW
========================================= */
const TrendingRow = ({
	item,
	index,
	icons,
}: {
	item: TrendingItem;
	index: number;
	icons: SearchIcons;
}) => {
	const title = typeof item === "string" ? item : item.title;
	const redirectUrl = typeof item === "string" ? "/" : (item.redirect_url || "/");

	return (
		<Link
			href={redirectUrl}
			className="flex items-start justify-between py-3 px-2 rounded-lg hover:bg-[#0300400D] transition-colors duration-200"
		>
			<div className="flex flex-1 min-w-0 gap-2 items-start">
				<span className="font-roboto font-bold text-[14px] leading-[20px] text-[#737685] w-[10%] flex-shrink-0">
					{String(index + 1).padStart(2, "0")}
				</span>
				<span className="font-roboto font-medium text-[16px] leading-[20px] text-[#141B2C] w-[80%] break-words">
					{title}
				</span>
			</div>
			<div className="w-[10%] flex justify-end flex-shrink-0">
				<Arrow icons={icons} />
			</div>
		</Link>
	);
};

/* =========================================
   DESKTOP — STEP 1
========================================= */
const Step1 = ({
	query,
	data,
	isTyping,
}: {
	query: string;
	data: SearchDropdownData;
	isTyping: boolean;
}) => {
	const noMatch =
		!isTyping && query.trim().length > 2 && !hasStep2Content(data.step2Data);

	return (
		<div className="w-[711px] rounded-[12px] overflow-hidden">
			{noMatch && <NoResultBanner query={query} icons={data.icons} />}
			<div className="flex">
				{data.sponsoredData.length > 0 && (
					<div className="w-[306px] flex-shrink-0 border-r border-gray-200 p-[24px] bg-[#007AFF0D]">
						<p className="font-roboto font-bold text-[12px] leading-[16px] tracking-[1.2px] uppercase text-[#003D9B] mb-3">
							Sponsored Highlights
						</p>
						{data.sponsoredData.map((item) => (
							<SponsoredCard key={item.title} item={item} />
						))}
					</div>
				)}
				
				{data.trendingData.length > 0 && (
					<div className="flex-1 p-4">
						<p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">
							Trending Searches
						</p>
						{data.trendingData.map((item, index) => (
							<TrendingRow
								key={typeof item === "string" ? item : `${item.title}-${index}`}
								item={item}
								index={index}
								icons={data.icons}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

/* =========================================
   DESKTOP — ROW
========================================= */
const Row = ({
	item,
	leftIcon,
	boxed = false,
	showArrow = false,
	query,
	icons,
}: {
	item: SimpleLinkItem;
	leftIcon: string;
	boxed?: boolean;
	showArrow?: boolean;
	query: string;
	icons: SearchIcons;
}) => (
	<Link
		href={item.link}
		className="flex items-center justify-between py-3 hover:bg-[#008C8B0D] transition-colors duration-200 hover:-mx-4 hover:px-4"
	>
		<div className="flex items-center gap-2 min-w-0">
			{/* <IconBox item={item} leftIcon={leftIcon} boxed={boxed} /> */}
			<HighlightMatch text={item.label} query={query} />
		</div>
		{showArrow && <RightArrow icons={icons} />}
	</Link>
);

/* =========================================
   DESKTOP — STEP 2
========================================= */
const Step2 = ({
	query,
	data,
}: {
	query: string;
	data: SearchDropdownData;
}) => {
	const { icons, step2Data, sectionRanking } = data;
	const sections = getOrderedStep2Sections(step2Data, sectionRanking);

	return (
		<div className="rounded-[12px] overflow-hidden p-4 w-[711px]">
			{sections.map((section, index) =>
				renderStep2Section(section, query, icons, false, index > 0)
			)}
		</div>
	);
};

/* =========================================
   MOBILE — NO RESULT BANNER
========================================= */
const MobileNoResultBanner = ({ query }: { query: string }) => (
	<div className="bg-[#3D3D3D] text-white rounded-xl p-4 mb-4 text-center">
		<p className="font-bold text-[16px] text-white mb-1">No Results Found</p>
		<p className="font-roboto font-normal text-[12px] leading-[20px] tracking-normal text-center">
			We couldn&apos;t find anything matching
			<br />
			<span className="font-bold text-white">&quot;{query}&quot;</span>
			<br />
			Try a different Keyword.
		</p>
	</div>
);

/* =========================================
   MOBILE — SPONSORED CARD
========================================= */
const MobileSponsoredCard = ({ item }: { item: SponsoredItem }) => (
	<div className="flex-shrink-0 border border-[#007AFF26] rounded-xl p-3 bg-white flex items-center gap-3">
		<Link href={item.redirect_url || "/"}>
			<ImageWrapper
				src={item.img}
				alt={item.title}
				width={55}
				height={50}
				className="w-[55px] h-[50px] object-fill rounded-lg flex-shrink-0"
			/>
		</Link>
		<div className="min-w-0">
			<p className="text-[9px] font-bold uppercase tracking-wider text-[#008C8B]">
				{item.label}
			</p>
			<Link
				href={item.redirect_url || "/"}
				className="font-roboto font-medium text-[14px] leading-[20px] text-[#141B2C] whitespace-nowrap"
			>
				{item.title}
			</Link>
			{item.subtitle && (
				<p className="text-[11px] text-gray-400 mt-0.5">{item.subtitle}</p>
			)}
		</div>
	</div>
);

/* =========================================
   MOBILE — TRENDING ROW
========================================= */
const MobileTrendingRow = ({
	item,
	index,
	icons,
}: {
	item: TrendingItem;
	index: number;
	icons: SearchIcons;
}) => {
	const title = typeof item === "string" ? item : item.title;
	const redirectUrl = typeof item === "string" ? "/" : (item.redirect_url || "/");

	return (
		<Link
			href={redirectUrl}
			className="flex items-start justify-between py-3 px-2 rounded-lg transition-colors duration-200 bg-[#fff] hover:bg-[#0300400D]"
		>
			<div className="flex items-center gap-2 min-w-0">
				<span className="text-xs text-gray-400 w-6 flex-shrink-0">
					{String(index + 1).padStart(2, "0")}
				</span>
				<span className="font-roboto font-[500] text-[13px] leading-[24px] text-[#141B2C] whitespace-wrap">
					{title}
				</span>
			</div>
			<Arrow icons={icons} />
		</Link>
	);
};

/* =========================================
   MOBILE — STEP 1
========================================= */
const MobileStep1 = ({
	query,
	data,
	isTyping,
}: {
	query: string;
	data: SearchDropdownData;
	isTyping: boolean;
}) => {
	const noMatch =
		!isTyping && query.trim().length > 2 && !hasStep2Content(data.step2Data);

	return (
		<div className="p-4 pt-0">
			{noMatch && <MobileNoResultBanner query={query} />}

			{data.sponsoredData.length > 0 && (
				<div className="-mx-4 bg-[#007AFF0D] py-3 mb-4">
					<h2 className="text-xs font-bold text-blue-600 tracking-widest uppercase mb-3 px-4">
						Sponsored Highlights
					</h2>
					<div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 px-4">
						{data.sponsoredData.map((item) => (
							<MobileSponsoredCard key={item.title} item={item} />
						))}
					</div>
				</div>
			)}

			{data.trendingData.length > 0 && (
				<>
					<h2 className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-1">
						Trending Searches
					</h2>
					{data.trendingData.map((item, index) => (
						<MobileTrendingRow
							key={typeof item === "string" ? item : `${item.title}-${index}`}
							item={item}
							index={index}
							icons={data.icons}
						/>
					))}
				</>
			)}
		</div>
	);
};

/* =========================================
   MOBILE — ROW
========================================= */
const MobileRow = ({
	item,
	leftIcon,
	boxed = false,
	showArrow = false,
	query,
	icons,
}: {
	item: SimpleLinkItem;
	leftIcon: string;
	boxed?: boolean;
	showArrow?: boolean;
	query: string;
	icons: SearchIcons;
}) => (
	<Link
		href={item.link}
		className="flex items-center justify-between py-2 md:py-3 px-1 hover:bg-[#008C8B0D] transition-colors duration-200 hover:-mx-2 hover:px-3"
	>
		<div className="flex items-center gap-2 min-w-0">
			{/* <IconBox item={item} leftIcon={leftIcon} boxed={boxed} /> */}
			<HighlightMatch text={item.label} query={query} />
		</div>
		{showArrow && <RightArrow icons={icons} />}
	</Link>
);

/* =========================================
   MOBILE — STEP 2
========================================= */
const MobileStep2 = ({
	query,
	data,
}: {
	query: string;
	data: SearchDropdownData;
}) => {
	const { icons, step2Data, sectionRanking } = data;
	const sections = getOrderedStep2Sections(step2Data, sectionRanking);

	return (
		<div className="p-4">
			{sections.map((section, index) =>
				renderStep2Section(section, query, icons, true, index > 0)
			)}
		</div>
	);
};

/* =========================================
   MAIN EXPORT
========================================= */
const SearchDropdownRevamp = ({
	query = "",
	isMobile = false,
	isTyping = false,
	data,
	onOptionClick,
}: SearchDropdownRevampProps) => {
	const variant = hasStep2Content(data.step2Data) ? "step2" : "step1";

	const handleOptionClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!onOptionClick) return;

		const target = event.target as HTMLElement;
		if (target.closest("a")) {
			onOptionClick();
		}
	};

	if (isMobile) {
		return (
			<div
				className="w-full bg-white rounded-[12px]"
				onClickCapture={handleOptionClickCapture}
			>
				{variant === "step1" ? (
					<MobileStep1 query={query} data={data} isTyping={isTyping} />
				) : (
					<MobileStep2 query={query} data={data} />
				)}
			</div>
		);
	}

	return (
		<div
			className="absolute top-full left-0 md:left-[-150px] lg:left-0 bg-white/85 backdrop-blur-[32px] shadow-[0px_12px_24px_rgba(20,27,44,0.15)] rounded-[12px] z-[999] max-h-[645px] overflow-y-auto no-scrollbar"
			onClickCapture={handleOptionClickCapture}
		>
			{variant === "step1" ? (
				<Step1 query={query} data={data} isTyping={isTyping} />
			) : (
				<Step2 query={query} data={data} />
			)}
		</div>
	);
};

export default SearchDropdownRevamp;